type ImSocketPayload = Record<string, unknown>;
type ImSocketCallback = (payload: ImSocketPayload) => void;

type SocketLike = {
  connected?: boolean;
  connect?: () => void;
  emit: (event: string, payload?: unknown, callback?: (response?: { ok?: boolean; message?: string }) => void) => void;
  on: (event: string, callback: (...args: unknown[]) => void) => void;
  io?: {
    engine?: {
      on?: (event: string, callback: (...args: unknown[]) => void) => void;
    };
  };
};

type SocketIoFactory = (url: string, options: Record<string, unknown>) => SocketLike;

declare global {
  interface Window {
    io?: SocketIoFactory;
    frontV2ImSocket?: SocketLike;
    frontV2ImSocketReady?: boolean;
  }
}

let socket: SocketLike | null = null;
let socketReady = false;
let socketInitializing = false;
let socketJoined = false;
let tokenInFlight = false;
let joinInFlight = false;
let token = '';
let handlersBound = false;
const callbacks: Array<(socket: SocketLike) => void> = [];
let currentSocketStatus: { status: string; message: string } = { status: 'idle', message: '' };

const socketOptions = {
  path: '/socket.io',
  transports: ['polling', 'websocket'],
  upgrade: true,
  rememberUpgrade: true,
  forceNew: true,
  reconnection: true,
  reconnectionAttempts: Infinity,
  reconnectionDelay: 800,
  reconnectionDelayMax: 5000,
  timeout: 12000
};

function dispatchImEvent(name: string, detail: ImSocketPayload = {}) {
  window.dispatchEvent(new CustomEvent(`front-v2:im-${name}`, { detail }));
}

function setStatus(status: string, message = '') {
  currentSocketStatus = { status, message };
  dispatchImEvent('socket-status', { status, message });
}

function loadSocketIoClient() {
  if (window.io) return Promise.resolve();
  const existing = document.querySelector<HTMLScriptElement>('script[data-front-v2-socket-io]');
  if (existing) {
    return new Promise<void>((resolve, reject) => {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('socket.io load failed')), { once: true });
    });
  }
  return new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = '/cdnjs/socket.io/2.2.0/socket.io.js';
    script.async = true;
    script.dataset.frontV2SocketIo = 'true';
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('socket.io load failed'));
    document.head.appendChild(script);
  });
}

function bindSocketHandlers(currentSocket: SocketLike) {
  if (handlersBound) return;
  handlersBound = true;

  currentSocket.on('unread_count_update', (payload) => {
    dispatchImEvent('unread-count-update', (payload as ImSocketPayload) || {});
  });
  currentSocket.on('new_message', (payload) => {
    dispatchImEvent('new-message', (payload as ImSocketPayload) || {});
  });
  currentSocket.on('conversation_read', (payload) => {
    dispatchImEvent('conversation-read', (payload as ImSocketPayload) || {});
  });
  currentSocket.on('online_status', (payload) => {
    dispatchImEvent('online-status', (payload as ImSocketPayload) || {});
  });
}

function notifyReady(currentSocket: SocketLike) {
  socketReady = true;
  socketJoined = true;
  socketInitializing = false;
  joinInFlight = false;
  window.frontV2ImSocket = currentSocket;
  window.frontV2ImSocketReady = true;
  bindSocketHandlers(currentSocket);
  setStatus('connected', 'IM 已连接');
  dispatchImEvent('socket-ready', {});
  const pending = callbacks.splice(0, callbacks.length);
  pending.forEach((callback) => {
    try {
      callback(currentSocket);
    } catch (e) {
      // Individual callbacks should not break the shared socket.
    }
  });
}

function joinSocket(currentSocket: SocketLike) {
  if (!currentSocket.connected || !token || socketJoined || joinInFlight) return;
  joinInFlight = true;
  setStatus('joining', '正在加入 IM 通道');
  let joined = false;
  const joinTimer = window.setTimeout(() => {
    if (joined || socketJoined) return;
    joinInFlight = false;
    socketReady = false;
    window.frontV2ImSocketReady = false;
    setStatus('connecting', 'IM 正在重试加入通道');
    joinSocket(currentSocket);
  }, 8000);

  currentSocket.emit('join', token, (response) => {
    joined = true;
    window.clearTimeout(joinTimer);
    joinInFlight = false;
    if (!response?.ok) {
      socketReady = false;
      socketJoined = false;
      window.frontV2ImSocketReady = false;
      setStatus('connecting', response?.message || 'IM 加入通道失败，正在重试');
      window.setTimeout(() => joinSocket(currentSocket), 1200);
      return;
    }
    notifyReady(currentSocket);
  });
}

export async function refreshFrontV2ImUnreadCount() {
  const response = await fetch('/api/im/unread_count', { credentials: 'include' });
  const payload = await response.json().catch(() => null);
  if (!response.ok || !payload?.success) throw new Error(payload?.error || '未读数加载失败');
  dispatchImEvent('unread-count-update', payload);
  return payload as { count: number; message_count: number; system_count: number };
}

export async function ensureFrontV2ImSocket(callback?: (socket: SocketLike) => void) {
  if (callback) {
    if (socketReady && socket) {
      callback(socket);
      return socket;
    }
    callbacks.push(callback);
  }
  if (socketReady || socketInitializing || tokenInFlight) return socket;
  await loadSocketIoClient();
  if (socketReady || socketInitializing || tokenInFlight) return socket;
  if (!window.io) throw new Error('socket.io unavailable');

  if (socket?.connected) {
    if (socketJoined) {
      notifyReady(socket);
    } else {
      joinSocket(socket);
    }
    return socket;
  }

  if (socket) {
    setStatus('connecting', 'IM 正在重连');
    socket.connect?.();
    return socket;
  }

  socketInitializing = true;
  tokenInFlight = true;
  setStatus('connecting', '正在连接 IM 服务');
  try {
    const response = await fetch('/api/im/socket_token', { credentials: 'include' });
    const payload = await response.json().catch(() => null);
    if (!response.ok || !payload?.success || !payload.token) throw new Error(payload?.error || '获取 IM 令牌失败');
    token = payload.token;
    tokenInFlight = false;
    socket = window.io(`${window.location.origin}/im`, socketOptions);
    window.frontV2ImSocket = socket;

    socket.on('connect', () => {
      socketReady = false;
      socketJoined = false;
      joinInFlight = false;
      window.frontV2ImSocketReady = false;
      joinSocket(socket as SocketLike);
    });
    socket.on('disconnect', () => {
      socketReady = false;
      socketJoined = false;
      joinInFlight = false;
      window.frontV2ImSocketReady = false;
      setStatus('connecting', 'IM 正在恢复连接');
    });
    socket.on('connect_error', () => {
      socketReady = false;
      socketJoined = false;
      joinInFlight = false;
      socketInitializing = false;
      window.frontV2ImSocketReady = false;
      setStatus('connecting', 'IM 正在重连');
    });
    socket.on('reconnect', () => {
      joinSocket(socket as SocketLike);
    });
    return socket;
  } catch (error) {
    tokenInFlight = false;
    socketInitializing = false;
    setStatus('error', error instanceof Error ? error.message : 'IM 连接失败');
    throw error;
  }
}

export async function queryFrontV2ImPresence(userIds: number[]) {
  const ids = Array.from(new Set(userIds.map((id) => Number(id)).filter((id) => Number.isInteger(id) && id > 0))).slice(0, 100);
  if (!ids.length) return {};
  const currentSocket = await ensureFrontV2ImSocket();
  if (!currentSocket) return {};
  return new Promise<Record<string, boolean>>((resolve) => {
    currentSocket.emit('presence_query', ids, (response) => {
      if (response?.ok && typeof (response as { statuses?: unknown }).statuses === 'object') {
        resolve(((response as { statuses?: Record<string, boolean> }).statuses) || {});
        return;
      }
      resolve({});
    });
  });
}

export function onFrontV2ImEvent(name: string, callback: ImSocketCallback) {
  const listener = (event: Event) => {
    callback((event as CustomEvent<ImSocketPayload>).detail || {});
  };
  window.addEventListener(`front-v2:im-${name}`, listener);
  // The shared socket can finish connecting before the Messages page mounts.
  // Replay its current state so the page does not remain on the initial label.
  if (name === 'socket-status' && currentSocketStatus.status !== 'idle') {
    callback({ ...currentSocketStatus });
  }
  return () => window.removeEventListener(`front-v2:im-${name}`, listener);
}
